/**
 * MigrationJobDetailPanel — slide-over panel for a single migration job.
 *
 * Spec §16.13: progress + per-folder breakdown + lifecycle controls.
 *
 * PR 13 ships:
 *   - status badge + counters + progress bar (auto-refreshing via TanStack
 *     Query, polling every 5s while the job is in flight)
 *   - Validate button (POST /validate, surfaces ValidationReport)
 *   - Cancel button (POST /cancel)
 *   - Promote Canary button (only visible when status=awaiting_canary_approval;
 *     opens the MigrationCanaryReviewModal)
 *   - Pause / Resume buttons stubbed-disabled with "ships in a later release"
 *     tooltips (backend stubs return 501 today)
 *   - Validation + verification reports rendered as collapsible sections
 *
 * Per-folder breakdown depends on the verification report (PR 22) so PR 13
 * surfaces overall counters today and the per-folder view fills in
 * automatically when verification_report is populated.
 */
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  useMigrationJob,
  useCancelMigrationJob,
  useValidateMigrationJob,
  usePauseMigrationJob,
  useResumeMigrationJob,
  migrationQueryKeys,
} from '../../hooks/useMigrationJobs';
import type { MigrationJob, MigrationJobStatus } from '../../types/migration.types';
import { MigrationCanaryReviewModal } from '../modals/MigrationCanaryReviewModal';
import { ShowSourceArchiveModal } from '../modals/ShowSourceArchiveModal';
import { EdgeMailAdminAPI } from '../../services/EdgeMailAdminAPI';

const ACTIVE_STATUSES: MigrationJobStatus[] = ['queued', 'validating', 'running', 'paused', 'awaiting_canary_approval'];

function statusClasses(status: MigrationJobStatus): string {
  switch (status) {
    case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'running': case 'validating': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'paused': case 'awaiting_canary_approval': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'failed': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    case 'cancelled': return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    case 'queued':
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}

function formatBytes(b: number | string | undefined | null): string {
  // Postgres bigint columns return as strings via node-pg by default —
  // coerce explicitly so `"0"` doesn't sneak past the truthy check below.
  const n = typeof b === 'string' ? Number(b) : (b ?? NaN);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

interface MigrationJobDetailPanelProps {
  jobId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function MigrationJobDetailPanel({ jobId, isOpen, onClose }: MigrationJobDetailPanelProps) {
  const queryClient = useQueryClient();
  const [canaryOpen, setCanaryOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [inspectMessageId, setInspectMessageId] = useState<string>('');
  const [sourceArchiveOpenForId, setSourceArchiveOpenForId] = useState<string | null>(null);

  const { data, isLoading, refetch } = useMigrationJob(isOpen ? jobId : null);
  const job: MigrationJob | undefined = data?.job;

  const cancelMutation = useCancelMigrationJob();
  const validateMutation = useValidateMigrationJob();
  const pauseMutation = usePauseMigrationJob();
  const resumeMutation = useResumeMigrationJob();

  // Auto-refresh every 5s while the job is active.
  React.useEffect(() => {
    if (!isOpen || !jobId) return;
    if (!job || !ACTIVE_STATUSES.includes(job.status)) return;
    const handle = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: migrationQueryKeys.job(jobId) });
    }, 5000);
    return () => clearInterval(handle);
  }, [isOpen, jobId, job?.status, queryClient]);

  if (!isOpen) return null;

  const handleAction = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
      refetch();
    } catch (err) {
      const msg = err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Action failed';
      setActionError(msg);
    }
  };

  const total = job?.total_estimated || 0;
  const processed = job?.processed || 0;
  const progressPct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <>
      {/* Slide-over backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Slide-over content */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white dark:bg-gray-900 shadow-xl border-l border-gray-200 dark:border-gray-800 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Migration job</h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{jobId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {isLoading && (
            <div className="text-center text-gray-500 py-12">
              <ArrowPathIcon className="h-5 w-5 mx-auto animate-spin" />
              <div className="mt-2 text-sm">Loading…</div>
            </div>
          )}
          {!isLoading && !job && (
            <div className="text-center text-rose-700 dark:text-rose-300 py-8">Job not found.</div>
          )}

          {job && (
            <>
              {/* Status + counters */}
              <div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusClasses(job.status)}`}>
                    {job.status}
                  </span>
                  <span className="text-sm text-gray-500">{job.source_type}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Stat label="Imported" value={job.imported.toLocaleString()} />
                  <Stat label="Deduped" value={job.deduped.toLocaleString()} />
                  <Stat label="Failed" value={job.failed.toLocaleString()} />
                  <Stat label="Processed" value={`${job.processed.toLocaleString()} / ${total.toLocaleString()}`} />
                </div>

                {total > 0 && (
                  <div className="mt-3">
                    <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                      <div
                        className="h-full bg-lex-accent transition-all"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{progressPct}%</div>
                  </div>
                )}
              </div>

              {/* Lifecycle controls */}
              <div className="flex flex-wrap gap-2">
                <ActionButton
                  icon={DocumentMagnifyingGlassIcon}
                  label="Validate"
                  onClick={() => handleAction(() => validateMutation.mutateAsync(job.job_id))}
                  busy={validateMutation.isPending}
                  disabled={!['queued', 'paused', 'failed'].includes(job.status)}
                />
                {job.status === 'awaiting_canary_approval' && (
                  <ActionButton
                    icon={RocketLaunchIcon}
                    label="Review canary"
                    onClick={() => setCanaryOpen(true)}
                    primary
                  />
                )}
                <ActionButton
                  icon={PauseIcon}
                  label="Pause"
                  onClick={() => handleAction(() => pauseMutation.mutateAsync(job.job_id))}
                  busy={pauseMutation.isPending}
                  disabled={true}
                  tooltip="Ships in a later release"
                />
                <ActionButton
                  icon={PlayIcon}
                  label="Resume"
                  onClick={() => handleAction(() => resumeMutation.mutateAsync(job.job_id))}
                  busy={resumeMutation.isPending}
                  disabled={true}
                  tooltip="Ships in a later release"
                />
                <ActionButton
                  icon={StopIcon}
                  label="Cancel"
                  onClick={() => handleAction(() => cancelMutation.mutateAsync(job.job_id))}
                  busy={cancelMutation.isPending}
                  disabled={['completed', 'cancelled', 'failed'].includes(job.status)}
                  destructive
                />
              </div>

              {actionError && (
                <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 rounded">
                  {actionError}
                </div>
              )}

              {/* Job metadata */}
              <Section title="Job">
                <Row label="Created" value={formatDate(job.created_at)} />
                <Row label="Started" value={formatDate(job.started_at)} />
                <Row label="Completed" value={formatDate(job.completed_at)} />
                <Row label="Source bytes" value={formatBytes(job.source_bytes_total)} />
                <Row label="Sink" value={job.options?.sink ?? '—'} />
                <Row label="Canary mode" value={job.options?.canary_only ? `On (${job.options.canary_size})` : 'Off'} />
                <Row label="Quiet mode" value={job.options?.quiet_mode ? 'On' : 'Off'} />
                {job.failure_reason && (
                  <Row label="Failure" value={job.failure_reason} />
                )}
              </Section>

              {/* Validation report */}
              {job.validation_report && (
                <CollapsibleSection title="Validation report">
                  <Row label="Readable" value={job.validation_report.readable ? 'Yes' : 'No'} />
                  <Row label="Format" value={job.validation_report.format_generation || '—'} />
                  <Row label="Estimated messages" value={String(job.validation_report.estimated_messages)} />
                  <Row label="Estimated folders" value={String(job.validation_report.estimated_folders)} />
                  <Row label="Estimated dedup hits" value={String(job.validation_report.estimated_dedup_hits)} />
                  <Row label="Estimated duration" value={`${job.validation_report.estimated_duration_seconds}s`} />
                  <Row label="Detected charset" value={job.validation_report.default_charset_detected} />
                  {job.validation_report.warnings?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-500 mb-1">Warnings</div>
                      <ul className="list-disc pl-5 space-y-0.5 text-xs text-gray-700 dark:text-gray-300">
                        {job.validation_report.warnings.map((w, i) => (
                          <li key={i}>
                            <span className="font-mono text-amber-600 dark:text-amber-400">{w.code}</span> — {w.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* Inspect a specific message — opens ShowSourceArchiveModal (PR 15) */}
              <Section title="Inspect message (forensic view)">
                <p className="text-xs text-gray-500 mb-2">
                  Paste a <code className="font-mono">message_id</code> (from the messages table or canary preview) to see the chain-of-custody record: source path, hash chain, diff summary.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={inspectMessageId}
                    onChange={(e) => setInspectMessageId(e.target.value)}
                    placeholder="message_id (uuid)"
                    className="flex-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1 text-xs font-mono"
                  />
                  <button
                    onClick={() => setSourceArchiveOpenForId(inspectMessageId.trim() || null)}
                    disabled={!inspectMessageId.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs bg-lex-accent text-white hover:bg-lex-accent-strong disabled:opacity-40"
                  >
                    Open
                  </button>
                </div>
              </Section>

              {/* Verification report — PR 15 + PR 22 */}
              {job.verification_report && (
                <CollapsibleSection title="Verification report">
                  {job.verification_report.meets_slo !== undefined && (
                    <Row
                      label="Acceptance SLO (≥99%)"
                      value={job.verification_report.meets_slo ? '✅ Met' : '❌ Below threshold'}
                    />
                  )}
                  {job.verification_report.import_rate_pct !== undefined && (
                    <Row label="Import rate" value={`${job.verification_report.import_rate_pct}%`} />
                  )}
                  {job.verification_report.size_check && (
                    <>
                      <Row
                        label="Source bytes"
                        value={job.verification_report.size_check.source_bytes.toLocaleString()}
                      />
                      <Row
                        label="Imported bytes"
                        value={job.verification_report.size_check.imported_bytes.toLocaleString()}
                      />
                      <Row
                        label="Size ratio"
                        value={
                          job.verification_report.size_check.size_ratio_pct < 0
                            ? 'unknown (no source size declared)'
                            : `${job.verification_report.size_check.size_ratio_pct}%`
                        }
                      />
                    </>
                  )}
                  {job.verification_report.attachment_integrity && (
                    <Row
                      label="Attachment integrity"
                      value={`${job.verification_report.attachment_integrity.integrity_pct}% (sample of ${job.verification_report.attachment_integrity.sample_size})`}
                    />
                  )}
                  {job.verification_report.message_id_preservation && (
                    <Row
                      label="Message-ID preservation"
                      value={`${job.verification_report.message_id_preservation.preservation_pct}% (sample of ${job.verification_report.message_id_preservation.sample_size})`}
                    />
                  )}
                  {job.verification_report.audit_log_completeness && (
                    <Row
                      label="Audit log"
                      value={
                        job.verification_report.audit_log_completeness.is_complete
                          ? `Complete (${job.verification_report.audit_log_completeness.actual_rows} rows)`
                          : `Incomplete (${job.verification_report.audit_log_completeness.actual_rows} of ≥${job.verification_report.audit_log_completeness.expected_rows_lower_bound} expected)`
                      }
                    />
                  )}
                  <Row
                    label="Threads reconstructed"
                    value={String(job.verification_report.threading_quality?.reconstructed_threads ?? 0)}
                  />
                  <Row
                    label="Thread islands"
                    value={String(job.verification_report.threading_quality?.thread_islands ?? 0)}
                  />
                  <Row label="Generated" value={formatDate(job.verification_report.generated_at)} />

                  {(job.verification_report.per_folder ?? job.verification_report.message_count_per_folder) && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-500 mb-1">Messages per folder</div>
                      <ul className="text-xs text-gray-700 dark:text-gray-300">
                        {Object.entries(
                          job.verification_report.per_folder ?? job.verification_report.message_count_per_folder ?? {},
                        ).map(([f, n]) => (
                          <li key={f}>
                            <span className="font-mono">{f}</span>: {(n as number).toLocaleString()}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!jobId) return;
                        const blob = await EdgeMailAdminAPI.getMigrationVerificationReportCsv(jobId);
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `verification-report-${jobId}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs bg-lex-accent text-white hover:bg-lex-accent-strong"
                    >
                      Download CSV (chain-of-custody)
                    </button>
                  </div>
                </CollapsibleSection>
              )}
            </>
          )}
        </div>
      </div>

      <MigrationCanaryReviewModal
        jobId={canaryOpen ? jobId : null}
        isOpen={canaryOpen}
        onClose={() => setCanaryOpen(false)}
        onActionComplete={() => {
          setCanaryOpen(false);
          refetch();
        }}
        targetMailboxEmail={job?.target_mailbox_email}
        previewMessageCount={job ? Number(job.imported) + Number(job.deduped) : undefined}
      />

      <ShowSourceArchiveModal
        messageId={sourceArchiveOpenForId}
        isOpen={!!sourceArchiveOpenForId}
        onClose={() => setSourceArchiveOpenForId(null)}
      />
    </>
  );
}

// ─── Tiny atoms ──────────────────────────────────────────────────────────
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-md p-2">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-base font-semibold text-gray-900 dark:text-white tabular-nums">{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 text-sm py-1">
      <div className="w-40 text-gray-500 dark:text-gray-400">{label}</div>
      <div className="flex-1 text-gray-900 dark:text-white break-all">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{title}</h3>
      <div>{children}</div>
    </div>
  );
}

function CollapsibleSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
        <span>{title}</span>
        <span>{open ? '−' : '+'}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

interface ActionButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  primary?: boolean;
  tooltip?: string;
}

function ActionButton({ icon: Icon, label, onClick, busy, disabled, destructive, primary, tooltip }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
        primary
          ? 'bg-lex-accent text-white hover:bg-lex-accent-strong'
          : destructive
            ? 'border border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/20'
            : 'border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <Icon className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
      {label}
    </button>
  );
}

export default MigrationJobDetailPanel;
